import { useTranslation } from "react-i18next";
import { Outlet, useLocation } from "react-router-dom";
import { useOrgRole, useProjectParams, type ProjectContext } from "./projectContext";
import { useProject } from "@/api/hooks/projects";
import { usePageCrumbs } from "@/app/layouts/shellContext";
import { ErrorNotice } from "@/components/ErrorNotice";
import { PageLoading } from "@/components/Spinner";
import { StatusPill } from "@/components/StatusPill";
import { NavTabs, type TabLink } from "@/components/ui/tabs";

const TAB_KEYS = ["questionnaire", "files", "feedback", "activity", "responses"] as const;

/** Project workspace: header, breadcrumb (Org › Project › Tab) and route tabs. */
export function ProjectLayout() {
  const { t } = useTranslation();
  const { org, project: projectSlug } = useProjectParams();
  const project = useProject(org, projectSlug);
  const role = useOrgRole(org);
  const location = useLocation();

  const base = `/${org}/${projectSlug}`;
  const rest = location.pathname.slice(base.length).split("/")[1] ?? "";
  const tabKey = (TAB_KEYS as readonly string[]).includes(rest) ? rest : "overview";
  usePageCrumbs(
    project.data
      ? [
          { label: project.data.organisation_name, to: `/${org}` },
          { label: project.data.name, to: base },
          { label: t(`project.tabs.${tabKey}`) },
        ]
      : [],
  );

  if (project.isPending) return <PageLoading />;
  if (project.isError) return <ErrorNotice error={project.error} />;

  const ctx: ProjectContext = { project: project.data, org, projectSlug, role };
  const pending = project.data.pending_required_count;
  const badge = pending > 0 ? pending : project.data.questionnaire_ready_to_submit ? 1 : 0;
  const tabs: TabLink[] = [
    { to: base, label: t("project.tabs.overview"), end: true },
    { to: `${base}/questionnaire`, label: t("project.tabs.questionnaire"), count: role === "staff" ? undefined : badge },
    { to: `${base}/files`, label: t("project.tabs.files") },
    { to: `${base}/feedback`, label: t("project.tabs.feedback") },
    { to: `${base}/activity`, label: t("project.tabs.activity") },
    ...(role === "staff" ? [{ to: `${base}/responses`, label: t("project.tabs.responses") }] : []),
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl">{project.data.name}</h1>
        <StatusPill status={project.data.status} />
      </div>
      <NavTabs label={t("project.tabs.label")} tabs={tabs} />
      <Outlet context={ctx} />
    </div>
  );
}
